import { ApiService, type ApiRequestConfig } from "./api.service";
import type {
  TRequestWorkflow,
  TRequestWorkflowApprovalResult,
  TRequestWorkflowPendingTransition,
  TRequestWorkflowRejectionResult,
  TRequestWorkflowTransition,
  TRequestWorkflowTransitionActivity,
} from "@/types/request-workflow";

export interface TRequestUpdateWorkflowStateData {
  [key: string]: unknown;
}

export interface TRequestCreateWorkflowTransitionData {
  workflow_id: string;
  transition_status_id: string;
  rejection_status_id?: string | null;
  required_approvals?: number | null;
}

export interface TRequestUpdateWorkflowTransitionData {
  transition_status_id?: string;
  rejection_status_id?: string | null;
  approval_status_id?: string | null;
  required_approvals?: number | null;
  require_approval?: boolean;
}

export interface TRequestSetWorkflowMoversData {
  mover_ids: string[];
}

export interface TRequestSetWorkflowApproversData {
  approver_ids: string[];
}

export interface TRequestWorkflowApprovalData {
  comment?: string | null;
  submission_id?: string;
}

export class RequestWorkflowService extends ApiService {
  private basePath(slug: string, formId: string): string {
    return `/api/workspaces/${slug}/intake/forms/${formId}`;
  }

  async listWorkflowStates(
    slug: string,
    formId: string,
    config?: ApiRequestConfig,
  ): Promise<TRequestWorkflow[]> {
    return this.get(`${this.basePath(slug, formId)}/workflow-states/`, config);
  }

  /**
   * Workflow states for MANY forms in one round-trip, keyed by form id.
   *
   * The requests list and board need every visible row's form rules; asking per
   * form fired one GET per distinct form on each /requests navigation (Sentry
   * NEXT-GEN-FRONTEND-2B). A form id that no longer resolves to a live form is
   * simply absent from the map rather than failing the batch, which is what used
   * to surface as an unhandled 404 (Sentry NEXT-GEN-FRONTEND-1K).
   */
  async listWorkflowStatesForForms(
    slug: string,
    formIds: string[],
    config?: ApiRequestConfig,
  ): Promise<Record<string, TRequestWorkflow[]>> {
    const query = new URLSearchParams({ form_ids: formIds.join(",") });
    return this.get(`/api/workspaces/${slug}/intake/workflow-states/?${query.toString()}`, config);
  }

  /** Pending transitions awaiting approval for a single submission. */
  async listPendingTransitions(
    slug: string,
    formId: string,
    submissionId: string,
  ): Promise<TRequestWorkflowPendingTransition[]> {
    return this.get(
      `${this.basePath(slug, formId)}/submissions/${submissionId}/pending-transitions/`,
    );
  }

  async updateWorkflowState(
    slug: string,
    formId: string,
    stateId: string,
    data: TRequestUpdateWorkflowStateData,
  ): Promise<TRequestWorkflow> {
    return this.patch(`${this.basePath(slug, formId)}/workflow-states/${stateId}/`, data);
  }

  async resetWorkflow(slug: string, formId: string): Promise<void> {
    return this.delete(`${this.basePath(slug, formId)}/workflow-reset/`);
  }

  async createTransition(
    slug: string,
    formId: string,
    data: TRequestCreateWorkflowTransitionData,
  ): Promise<TRequestWorkflowTransition> {
    return this.post(`${this.basePath(slug, formId)}/workflow-transitions/`, data);
  }

  async updateTransition(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestUpdateWorkflowTransitionData,
  ): Promise<TRequestWorkflowTransition> {
    return this.patch(`${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/`, data);
  }

  async deleteTransition(slug: string, formId: string, transitionId: string): Promise<void> {
    return this.delete(`${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/`);
  }

  async setMovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowMoversData,
  ): Promise<void> {
    return this.post(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/movers/`,
      data,
    );
  }

  async setApprovers(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestSetWorkflowApproversData,
  ): Promise<TRequestWorkflowTransition> {
    return this.post(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/approvers/`,
      data,
    );
  }

  async submitApproval(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
  ): Promise<TRequestWorkflowApprovalResult> {
    return this.post(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/approve/`,
      data,
    );
  }

  async submitRejection(
    slug: string,
    formId: string,
    transitionId: string,
    data: TRequestWorkflowApprovalData,
  ): Promise<TRequestWorkflowRejectionResult> {
    return this.post(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/reject/`,
      data,
    );
  }

  async requestApproval(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
  ): Promise<void> {
    return this.post(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/request/`,
      { submission_id: submissionId },
    );
  }

  async cancelApprovalRequest(
    slug: string,
    formId: string,
    transitionId: string,
    submissionId: string,
  ): Promise<void> {
    return this.delete(
      `${this.basePath(slug, formId)}/workflow-transitions/${transitionId}/request/`,
      { data: { submission_id: submissionId } },
    );
  }

  async listActivity(
    slug: string,
    formId: string,
    params?: Record<string, string>,
    config?: ApiRequestConfig,
  ): Promise<TRequestWorkflowTransitionActivity[]> {
    return this.get(`${this.basePath(slug, formId)}/workflow-activity/`, { ...config, params });
  }
}
